export type LibrarySection = "events" | "places" | "articles";

const LIBRARY_BASE_PATHS: Record<LibrarySection, string> = {
  events: "/events/library",
  places: "/places/library",
  articles: "/articles/library",
};

const STORAGE_PREFIX = "whart:library-search:";

export function librarySectionFromBasePath(
  basePath: string,
): LibrarySection | null {
  if (basePath === "/events") return "events";
  if (basePath === "/places") return "places";
  if (basePath === "/articles") return "articles";
  return null;
}

function storageKey(section: LibrarySection): string {
  return `${STORAGE_PREFIX}${section}`;
}

function normalizeSearch(search: string): string {
  if (!search) return "";
  return search.startsWith("?") ? search : `?${search}`;
}

export function hasActiveLibrarySearch(search: string): boolean {
  if (!search) return false;

  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );

  if (params.get("q")) return true;
  if (params.get("sort")) return true;
  if (params.get("size") && params.get("size") !== "20") return true;
  if (params.get("category") && params.get("category") !== "ALL") return true;
  if (
    params.get("start_from") ||
    params.get("start_to") ||
    params.get("end_from") ||
    params.get("end_to") ||
    params.get("start_on") ||
    params.get("end_on")
  ) {
    return true;
  }

  const status = params.get("status") ?? "ALL";
  const page = params.get("page") ?? "1";

  return status !== "ALL" || page !== "1";
}

export function syncLibrarySearchSnapshot(
  section: LibrarySection,
  search: string,
): void {
  if (typeof sessionStorage === "undefined") return;

  try {
    if (hasActiveLibrarySearch(search)) {
      sessionStorage.setItem(storageKey(section), normalizeSearch(search));
      return;
    }

    sessionStorage.removeItem(storageKey(section));
  } catch {
    // Ignore quota errors and private browsing restrictions.
  }
}

export function getLibrarySearchSnapshot(
  section: LibrarySection,
): string | null {
  if (typeof sessionStorage === "undefined") return null;

  try {
    return sessionStorage.getItem(storageKey(section));
  } catch {
    return null;
  }
}

export function getLibraryReturnHref(section: LibrarySection): string {
  const base = LIBRARY_BASE_PATHS[section];
  const snapshot = getLibrarySearchSnapshot(section);
  if (!snapshot) return base;
  return `${base}${snapshot}`;
}
